import React from 'react';
import { getNestedValue, getColorClasses, resolveConditionalColor, safeDisplayValue } from './utils';

/**
 * ProgressBarWidget - Renders a progress bar with gradient
 * 
 * Config structure:
 * {
 *   dataKey: "maintenance_progress",
 *   color: "orange",
 *   label: "진행률" (optional)
 * }
 */
export default function ProgressBarWidget({ config, data }) {
  if (!config) return null;

  const progress = safeDisplayValue(getNestedValue(data, config.dataKey, 0), 0);
  
  // Ensure progress is between 0 and 100
  const normalizedProgress = Math.min(Math.max(Number(progress) || 0, 0), 100);

  // Resolve color conditionally based on progress value
  const resolvedColor = resolveConditionalColor(normalizedProgress, config.color || 'orange', config.colorRules);
  const colors = getColorClasses(resolvedColor);

  // Get gradient colors based on the resolved color
  const gradientMap = {
    orange: 'from-orange-600 to-orange-400',
    red: 'from-red-600 to-red-400',
    emerald: 'from-emerald-600 to-emerald-400',
    indigo: 'from-indigo-600 to-indigo-400',
    slate: 'from-slate-600 to-slate-400',
    blue: 'from-blue-600 to-blue-400',
    purple: 'from-purple-600 to-purple-400',
    pink: 'from-pink-600 to-pink-400',
    cyan: 'from-cyan-600 to-cyan-400',
    yellow: 'from-yellow-600 to-yellow-400'
  };
  
  const shadowMap = {
    orange: 'rgba(245,158,11,0.4)',
    red: 'rgba(239,68,68,0.4)',
    emerald: 'rgba(16,185,129,0.4)',
    indigo: 'rgba(99,102,241,0.4)',
    slate: 'rgba(148,163,184,0.4)',
    blue: 'rgba(59,130,246,0.4)',
    purple: 'rgba(168,85,247,0.4)',
    pink: 'rgba(236,72,153,0.4)',
    cyan: 'rgba(6,182,212,0.4)',
    yellow: 'rgba(234,179,8,0.4)'
  };

  const gradient = gradientMap[resolvedColor] || gradientMap.orange;
  const shadow = shadowMap[resolvedColor] || shadowMap.orange;

  return (
    <div>
      {config.label && (
        <div className="flex items-center justify-between px-1 mb-2">
          <span className="text-[12px] text-slate-400 font-medium">{config.label}</span>
          <span className={`text-[13px] font-black ${colors.text}`}>{normalizedProgress}%</span>
        </div>
      )}
      <div className="relative h-2 bg-white/5 rounded-full overflow-hidden">
        <div 
          className={`absolute inset-0 bg-gradient-to-r ${gradient}`}
          style={{ 
            width: `${normalizedProgress}%`,
            boxShadow: `0 0 10px ${shadow}`
          }} 
        />
      </div>
    </div>
  );
}
